import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormatData } from "utils/FormatData";
import { dayjs } from "Config/dayjs";
import { projectsDataStore } from "Stores/ProjectsStore";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { API_URL } from "General/APIEndpoints";
import { API } from "General/API";
import { ButtonsRow } from "./Form/ButtonsRow";
import { Button } from "./General/Button";
import { CheckboxProps } from "antd/lib/checkbox";
import { DocumentsFetchParams, documentsSortByMap } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { ScrollableTable, ScrollableTableProps, TableColumnProps } from "./ScrollableTable/ScrollableTable";

interface ProjectDocumentsScrollTableProps
  extends WithTranslation,
    Omit<ScrollableTableProps<{}>, "columns" | "data" | "rowKey"> {
  initialValue?: API_FileDataEntity[];
  onAddFiles: (files: API_FileDataEntity[]) => void;
  onCancel: () => void;
  getCheckboxProps?: (rowData: API_FileDataEntity) => CheckboxProps;
}

interface ProjectDocumentsScrollTableState {
  currentFolderId: number;
  data: Array<API_FileDataEntity>;
  loading: boolean;
  selectedRows: API_FileDataEntity[];
  sortBy: keyof API_FileDataEntity;
  sortOrder: API_DOCUMENTSORTING_SORTORDER;
}

export const ProjectDocumentsScrollTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ProjectDocumentsScrollTableProps, ProjectDocumentsScrollTableState> {
      constructor(props: ProjectDocumentsScrollTableProps) {
        super(props);
        this.state = {
          currentFolderId: undefined,
          data: [],
          loading: false,
          selectedRows: props.initialValue || [],
          sortBy: "name",
          sortOrder: "ASC",
        };
      }

      componentDidMount() {
        (async () => await this.loadData())();
      }

      loadData = async (folderId?: number) => {
        try {
          this.setState({ loading: true });
          const projectId = projectsDataStore.projectId;
          const parsedFolderId =
            folderId && String(folderId).indexOf("F") !== -1 ? Number(folderId.toString().substring(1)) : folderId;
          const url = folderId
            ? API_URL.DOCUMENTS.getDocumentsByFolderId(projectId, parsedFolderId)
            : API_URL.DOCUMENTS.getDocumentsOfRootFolder(projectId);

          const params: DocumentsFetchParams = {
            sortBy: documentsSortByMap[this.state.sortBy],
            sortOrder: this.state.sortOrder,
            limit: 30,
          };

          const response: API_MultiDocumentResponse = await API.getData(url, params);
          let data = [];
          if (response.items.parentId) {
            data.push({ id: "F" + response.items.parentId, name: "...", type: "FOLDER" });
          }
          if (response.items.subfolders) {
            response.items.subfolders = response.items.subfolders.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (item) => (item.type === "FOLDER" ? { ...item, id: `F${item.id}` as any } : item)
            );
            data = data.concat(response.items.subfolders);
          }
          if (response.items.files) {
            data = data.concat(response.items.files);
          }
          this.setState({ data, currentFolderId: response.items.id });
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      };

      handleSort = (sortBy: keyof API_FileDataEntity, sortOrder: API_DOCUMENTSORTING_SORTORDER) => {
        try {
          this.setState({ sortBy, sortOrder }, async () => {
            await this.loadData(this.state.currentFolderId);
          });
          return Promise.resolve();
        } catch (error) {
          throw error;
        }
      };

      handleAddFiles = () => {
        this.props.onAddFiles(this.state.selectedRows);
      };

      handleSelectRows = ({ selectedRows }) => {
        this.setState({ selectedRows });
      };

      get selectedRowKeys() {
        return this.state.selectedRows.map((item) => item.id);
      }

      render() {
        const {
          onCancel,
          getCheckboxProps,
          t,
          onAddFiles: _onAddFiles,
          initialValue: _initialValue,
          ...restProps
        } = this.props;
        const columns: TableColumnProps<API_FileDataEntity>[] = [
          {
            dataKey: "type",
            width: 40,
            cellRenderer: ({ cellData }) => {
              return cellData === "FOLDER" ? (
                <FontAwesomeIcon icon={"folder"} size="lg" fixedWidth={true} />
              ) : (
                <FontAwesomeIcon icon={["far", "file"]} size="lg" fixedWidth={true} />
              );
            },
            disableSort: true,
          },
          {
            dataKey: "name",
            label: t("Filename"),
            width: 200,
            flexGrow: 1,
            // disableSort: true,
          },
          {
            dataKey: "size",
            label: t("Size"),
            width: 110,
            cellRenderer: ({ cellData }) => (cellData ? FormatData.formatBytes(cellData, 1) : null),
            // disableSort: true,
          },
          {
            dataKey: "created",
            label: t("Last modified"),
            width: 180,
            cellRenderer: ({ cellData }) => dayjs(cellData).format("lll"),
            // disableSort: true,
          },
        ];

        const { data, loading, sortBy, sortOrder } = this.state;

        return (
          <div className="scrollable-table-wrapper collection-table">
            <ScrollableTable<API_FileDataEntity>
              {...restProps}
              rowKey="id"
              data={data}
              loadMore={() => Promise.resolve()}
              rowCount={data.length}
              columns={columns}
              loading={loading}
              sortBy={sortBy}
              sortDirection={sortOrder}
              onSelectRows={this.handleSelectRows}
              selectedRowKeys={this.selectedRowKeys}
              onSort={this.handleSort}
              onRowClick={({ rowData }) => (rowData.type === "FOLDER" ? this.loadData(rowData.id) : undefined)}
              stretchHeight={true}
              getCheckboxProps={getCheckboxProps}
              onCheckAllPredicate={(record) => !record.type.startsWith("FO")} // cheap check for "is not of type FOLDER"
              draggableRows={true}
              resizeableHeaders={"PROJECT_FILES"}
              ignoreResizeableHeaders={["id", "created"]}
            />
            <ButtonsRow justify="space-between">
              <Button onClick={onCancel}>{t("Cancel")}</Button>
              <Button onClick={this.handleAddFiles} type="primary">
                {t("Add files")}
              </Button>
            </ButtonsRow>
          </div>
        );
      }
    }
  )
);
